import React, { useState } from 'react';
import {
    TextField,
    Typography,
    Box,
    Grid,
    IconButton,
    Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Translate, withLocalize } from 'react-localize-redux';
import CryptoJS from 'crypto-js';
import Modal from '../../general/modal';
import { ButtonContinue, ButtonCancel } from '../../general/mini_components';
import { decryptData, encryptData } from '../../../utils/index.jsx';
import {
    verifyOwnPasswordService,
    resetResearcherPasswordService,
    evaluatePasswordComplexity,
    isPasswordComplexEnough,
} from '../../../services/researcherSecurity';

/**
 * SHER-XYZ — admin "reset another researcher's password" flow.
 *
 * Three stages:
 *   1. re-auth     — admin re-enters their own password (defence against left-logged-in laptops).
 *   2. entering    — admin types the new password for the target user (with confirm + complexity gate).
 *                    On submit, we re-wrap the investigation key for the target client-side, send
 *                    the wrapped values + new password hash to the API.
 *   3. success     — show the typed plaintext back with a copy button so the admin can hand it
 *                    to the target out-of-band. The target will be forced to change it on first login.
 *
 * Encryption details (v1, two-level):
 *   - admin's session has rawKeyResearcher in localStorage (the password itself is never stored).
 *   - admin decrypts investigation.keyResearcherInvestigation with their rawKeyResearcher to get
 *     the plaintext investigation key.
 *   - we generate a fresh 32-byte rawKeyResearcher for the target, wrap it with the new plaintext
 *     password (matches the unwrap in authService.js on next login), and re-wrap the investigation
 *     key under that new rawKeyResearcher.
 */
function ResetPasswordFlow({ open, onClose, target, investigation, translate, onSuccess }) {
    const [stage, setStage] = useState('reauth'); // reauth | entering | submitting | success | error
    const [adminPassword, setAdminPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [copied, setCopied] = useState(false);

    function reset() {
        setStage('reauth');
        setAdminPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setErrorMessage(null);
        setCopied(false);
    }

    function handleClose() {
        reset();
        onClose();
    }

    async function handleVerifyAdmin() {
        setErrorMessage(null);
        if (!adminPassword) {
            setErrorMessage(translate('investigation.share.reset_password.error_password_required'));
            return;
        }
        try {
            const hash = CryptoJS.SHA256(adminPassword).toString(CryptoJS.enc.Base64);
            const response = await verifyOwnPasswordService(hash);
            if (response.status === 200) {
                setStage('entering');
            } else {
                setErrorMessage(translate('investigation.share.reset_password.error_wrong_password'));
            }
        } catch (err) {
            setErrorMessage(translate('investigation.share.reset_password.error_wrong_password'));
        }
    }

    async function handleSubmitNew() {
        setErrorMessage(null);
        if (!isPasswordComplexEnough(newPassword)) {
            setErrorMessage(translate('investigation.share.reset_password.error_complexity'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setErrorMessage(translate('investigation.share.reset_password.error_mismatch'));
            return;
        }
        setStage('submitting');
        try {
            // 1. Decrypt the investigation key with admin's session credentials.
            const adminRawKeyResearcher = localStorage.getItem('rawKeyResearcher');
            if (!adminRawKeyResearcher) throw new Error('Admin session missing rawKeyResearcher');
            const rawKeyInvestigation = decryptData(
                investigation.keyResearcherInvestigation,
                adminRawKeyResearcher,
            );

            // 2. Generate a fresh personal key for the target.
            const newTargetRawKeyResearcher = CryptoJS.lib.WordArray
                .random(32)
                .toString(CryptoJS.enc.Base64);

            // 3. Wrap the personal key with the new plaintext password (matches the unwrap
            //    in authService.js signIn: decryptData(payload.keyResearcher, password)).
            const newKeyEncrypted = encryptData(newTargetRawKeyResearcher, newPassword);

            // 4. Re-wrap the investigation key under the new personal key.
            const newKeyResearcherInvestigation = encryptData(
                rawKeyInvestigation,
                newTargetRawKeyResearcher,
            );

            // 5. Hash the new password for API auth lookup.
            const newPasswordSha256 = CryptoJS.SHA256(newPassword).toString(CryptoJS.enc.Base64);

            const complexityProof = evaluatePasswordComplexity(newPassword);

            await resetResearcherPasswordService(investigation.uuid, target.uuid, {
                newPasswordSha256,
                newKeyEncrypted,
                newKeyResearcherInvestigation,
                complexityProof,
            });

            setStage('success');
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Reset failed:', err);
            setStage('entering');
            setErrorMessage(
                err?.errorMessage ||
                err?.response?.data?.errorMessage ||
                translate('investigation.share.reset_password.error_generic'),
            );
        }
    }

    function handleCopyPassword() {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(newPassword);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    }

    const targetName = target ? `${target.name || ''} ${target.surnames || ''}`.trim() || target.email : '';

    function renderReauthStage() {
        return (
            <Grid container spacing={2} style={{ minWidth: 360 }}>
                <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom>
                        <Translate id="investigation.share.reset_password.reauth_intro" data={{ name: targetName }} />
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        type="password"
                        fullWidth
                        autoFocus
                        label={translate('investigation.share.reset_password.your_password')}
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleVerifyAdmin();
                        }}
                        data-testid="reset-password-admin"
                    />
                </Grid>
                {errorMessage && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="error">{errorMessage}</Typography>
                    </Grid>
                )}
                <Grid item xs={12} style={{ textAlign: 'right' }}>
                    <ButtonCancel onClick={handleClose}>
                        <Translate id="general.cancel" />
                    </ButtonCancel>
                    <ButtonContinue onClick={handleVerifyAdmin} data-testid="reset-password-verify">
                        <Translate id="general.continue" />
                    </ButtonContinue>
                </Grid>
            </Grid>
        );
    }

    function renderEnteringStage() {
        const proof = evaluatePasswordComplexity(newPassword);
        const checks = [
            { ok: proof.length >= 8, label: 'investigation.share.reset_password.req_length' },
            { ok: proof.hasUpper, label: 'investigation.share.reset_password.req_upper' },
            { ok: proof.hasNumber, label: 'investigation.share.reset_password.req_number' },
            { ok: proof.hasSymbol, label: 'investigation.share.reset_password.req_symbol' },
        ];
        const isSubmitting = stage === 'submitting';
        const canSubmit =
            !isSubmitting &&
            isPasswordComplexEnough(newPassword) &&
            newPassword === confirmPassword;
        return (
            <Grid container spacing={2} style={{ minWidth: 360 }}>
                <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom>
                        <Translate id="investigation.share.reset_password.entering_intro" data={{ name: targetName }} />
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        type="password"
                        fullWidth
                        autoFocus
                        label={translate('investigation.share.reset_password.new_password')}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        data-testid="reset-password-new"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        type="password"
                        fullWidth
                        label={translate('investigation.share.reset_password.confirm_password')}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && canSubmit) handleSubmitNew();
                        }}
                        data-testid="reset-password-confirm"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box>
                        {checks.map(({ ok, label }) => (
                            <Typography
                                key={label}
                                variant="body2"
                                style={{ color: ok ? 'green' : '#666' }}
                            >
                                {ok ? '✓' : '○'} <Translate id={label} />
                            </Typography>
                        ))}
                        {newPassword && confirmPassword && newPassword !== confirmPassword && (
                            <Typography variant="body2" color="error">
                                <Translate id="investigation.share.reset_password.error_mismatch" />
                            </Typography>
                        )}
                    </Box>
                </Grid>
                {errorMessage && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="error">{errorMessage}</Typography>
                    </Grid>
                )}
                <Grid item xs={12} style={{ textAlign: 'right' }}>
                    <ButtonCancel onClick={handleClose}>
                        <Translate id="general.cancel" />
                    </ButtonCancel>
                    <ButtonContinue
                        onClick={handleSubmitNew}
                        disabled={!canSubmit}
                        data-testid="reset-password-submit"
                    >
                        <Translate id={isSubmitting ? 'general.loading.loading' : 'general.confirm'} />
                    </ButtonContinue>
                </Grid>
            </Grid>
        );
    }

    function renderSuccessStage() {
        return (
            <Grid container spacing={2} style={{ minWidth: 360 }}>
                <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <CheckCircleIcon style={{ color: 'green', fontSize: 48 }} />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body1" gutterBottom>
                        <Translate id="investigation.share.reset_password.success_intro" data={{ name: targetName }} />
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        <Translate id="investigation.share.reset_password.success_warning" />
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            padding: '8px 12px',
                            background: '#fafafa',
                        }}
                    >
                        <Typography
                            variant="body1"
                            style={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}
                            data-testid="reset-password-success-value"
                        >
                            {newPassword}
                        </Typography>
                        <Tooltip title={copied ? translate('general.copied') : translate('general.copy')}>
                            <IconButton onClick={handleCopyPassword} size="small" data-testid="reset-password-copy">
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>
                <Grid item xs={12} style={{ textAlign: 'right' }}>
                    <ButtonContinue onClick={handleClose} data-testid="reset-password-close">
                        <Translate id="general.close" />
                    </ButtonContinue>
                </Grid>
            </Grid>
        );
    }

    let body;
    if (stage === 'reauth') body = renderReauthStage();
    else if (stage === 'entering' || stage === 'submitting') body = renderEnteringStage();
    else if (stage === 'success') body = renderSuccessStage();
    else body = null;

    return (
        <Modal
            open={open}
            closeModal={stage === 'submitting' ? undefined : handleClose}
            title={translate('investigation.share.reset_password.title')}
        >
            {body}
        </Modal>
    );
}

export default withLocalize(ResetPasswordFlow);
